import { useState, useEffect, useContext } from 'react';
import axios from 'axios';

import { useLazyDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';

import { getPricingForClosestStore } from '../utils/pricing-helper';
import NoPricingError from '../utils/NoPricingError';
import {
  PRICING_URL,
  TOOL_PRICE_DELIVERY_OPTIONS,
  TOOL_PRICE_OPTIONS,
  VEHICLE_PRICE_OPTIONS
} from '../utils/constants';

export const useApiData = ({
  refresh,
  experienceStore,
  storeList,
  rentalCategory,
  rentalSubCategory,
  isAVehicle,
  isDeliveryFlow,
  graphQlEnabled
}) => {

  const [pricingData, setPricingData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [pricingNotFound, setPricingNotFound] = useState(false);

  const ctx = useContext(ExperienceContext);
  const { hosts } = ctx;
  const { apionline } = hosts || {};
  let pricingUrl = '';

  let variables = {
    storeList: [],
    categoryCode: rentalCategory,
    subCategoryCode: rentalSubCategory
  };
  const [getRentalPricing, _data] = useLazyDataModel('getRentalPricing', {
    variables,
    ssr: !graphQlEnabled || storeList?.length <= 0 || !rentalCategory || !rentalSubCategory
  });

  const fetchData = async (mounted) => {
    setIsError(false);
    setIsLoading(true);
    try {
      const rentalStores = [...storeList].sort((storeA, storeB) => {
        return (
          Number.parseFloat(storeA.distance)
          - Number.parseFloat(storeB.distance)
        );
      });

      const rentalStoreIds = rentalStores.map((store) => store.storeId);

      pricingUrl = `${apionline}${PRICING_URL}/${rentalCategory}/${rentalSubCategory}/${rentalStoreIds.join(',')}`;

      let pricingResponse = null;

      if (graphQlEnabled) {

        variables.storeList = rentalStoreIds;
        const { data, error } = await getRentalPricing({ variables });

        if (error) {
          throw new Error(error);
        }

        pricingResponse = data?.getRentalPricing;

      } else {
        const {
          data: trioPricingResponse,
          status: pricingStatus,
          statusText: pricingStatusText
        } = await axios(pricingUrl);

        if (pricingStatus === 204) {
          throw new NoPricingError(`No pricing information found for URL: ${pricingUrl}`);
        }

        if (pricingStatus !== 200) {
          throw new Error(`statusCode: ${pricingStatus}\nstatusText: ${pricingStatusText}\nURL: ${pricingUrl}`);
        }

        pricingResponse = trioPricingResponse;

      }

      const rentalPricingData = (pricingResponse
        && pricingResponse?.map((store) => {
          const { locationNbr, depositAmt } = store;
          const rentalPricing = [];
          let priceOptions;

          if (isAVehicle) {
            priceOptions = VEHICLE_PRICE_OPTIONS;
          } else if (isDeliveryFlow) {
            priceOptions = TOOL_PRICE_DELIVERY_OPTIONS;
          } else {
            priceOptions = TOOL_PRICE_OPTIONS;
          }

          Object.entries(priceOptions).forEach(([optionKey, [duration = null, promotion = null]]) => {
            const priceOption = {};
            priceOption.price = parseFloat(store[optionKey]);
            priceOption.duration = duration;

            if (promotion) {
              priceOption.promotion = promotion;
            }
            rentalPricing.push(priceOption);
          });

          return {
            storeId: locationNbr,
            rentalDeposit: { depositAmount: parseFloat(depositAmt) },
            rentalPricing
          };
        })
      );

      if (mounted && rentalStores && rentalPricingData) {
        setPricingData(getPricingForClosestStore({
          experienceStore,
          rentalStores,
          rentalPricingData
        }));
      }
    } catch (error) {
      if (error instanceof NoPricingError) {
        setPricingNotFound(true);
      } else {
        setIsError(true);
        LIFE_CYCLE_EVENT_BUS.trigger('rental-api-sources.error', {
          rentalErrorType: 'RentalPricingComponent',
          rentalMessage: error?.message,
          rentalEndpoint: {
            pricingUrl
          }
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    if (storeList && rentalCategory && rentalSubCategory) {
      fetchData(mounted);
    }
    return () => { mounted = false; };
  }, [graphQlEnabled, storeList, rentalCategory, rentalSubCategory, isAVehicle, refresh]);

  return { pricingData, isLoading, pricingNotFound, isError };
};
